import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Euro, TrendingUp, FileText, Gauge, PieChart, KanbanSquare, Wrench, AlertCircle, CarFront, Target, FolderKanban } from "lucide-react";
import { fmtEUR } from "@/lib/format";
import { KpiCard, Widget, Priorites, Ligne, DetailModal, chargerDetailKpi, libPeriode, TendanceSub } from "@/components/dashboards/commun";

const STATUTS_ADM = {
  a_controler: "À contrôler", incomplet: "Incomplet", conforme: "Conforme",
  en_commande: "En commande", en_preparation: "En préparation", pret_a_livrer: "Prêt à livrer",
  livre_a_facturer: "Livré, à facturer", facture_a_encaisser: "Facturé, à encaisser", solde: "Soldé",
};

const ACTIVITES = [["vn", "VN", "#2F6FED"], ["vo", "VO", "#16A34A"], ["atelier", "Atelier", "#F59E0B"], ["pieces", "Pièces", "#E5484D"]];
const ETAPES = { detectee: "Détectée", contactee: "Contactée", offre: "Offre", gagnee: "Gagnée", perdue: "Perdue" };

const JaugeObjectif = ({ label, realise, objectif, pct, prorata, testId }) => {
  const c = pct == null ? "#94A3B8" : pct >= 100 ? "#16A34A" : pct >= 70 ? "#D97706" : "#DC2626";
  return (
    <div className="py-2" data-testid={testId}>
      <div className="flex items-center justify-between text-[12.5px] mb-1 gap-2">
        <span className="font-semibold">{label}</span>
        <span className="text-[--idc-muted] whitespace-nowrap">{realise} / {objectif} — <b style={{ color: c }}>{pct == null ? "—" : `${pct} %`}</b></span>
      </div>
      <div className="relative h-2.5 bg-[#E5EAF2] rounded-full">
        <div className="h-2.5 rounded-full" style={{ width: `${Math.min(pct ?? 0, 100)}%`, background: c }} />
        <div className="absolute -top-1 -bottom-1 w-[2px] rounded bg-[#0B1B33]/70" style={{ left: `${Math.min(prorata ?? 0, 100)}%` }}
          title={`Prorata du temps écoulé : ${prorata} %`} data-testid={`${testId}-prorata`} />
      </div>
    </div>
  );
};

export const DashDirecteur = ({ d }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const k = d.kpis, w = d.widgets;
  const ouvrir = (cle) => chargerDetailKpi(cle, setDetail, { site: d.site, periode: d.periode });
  return (
    <div data-testid="dash-directeur">
      {w.priorites && <Priorites items={w.priorites} titre="Synthèse dirigeant du jour" />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {k.ca_facture && <KpiCard icon={Euro} couleur="#2F6FED" titre={libPeriode(d.periode, "CA facturé")} valeur={fmtEUR(k.ca_facture.valeur)} testId="kpi-ca-facture" onClick={() => ouvrir("ca_facture")} sub={<TendanceSub kpi={k.ca_facture} periode={d.periode} />} />}
        {k.marge && <KpiCard icon={TrendingUp} couleur="#16A34A" titre={libPeriode(d.periode, "Marge véhicules")} valeur={fmtEUR(k.marge.valeur)} testId="kpi-marge" onClick={() => ouvrir("marge")} sub={<TendanceSub kpi={k.marge} periode={d.periode} />} />}
        {k.encours && <KpiCard icon={FileText} couleur="#E8853D" titre="Encours à facturer" valeur={fmtEUR(k.encours.valeur)} testId="kpi-encours" onClick={() => ouvrir("encours")} sub={<TendanceSub kpi={k.encours} periode={d.periode} />} />}
        {k.occupation_atelier && <KpiCard icon={Gauge} couleur="#7C5CE0" titre="Occupation atelier" valeur={`${k.occupation_atelier.valeur} %`} testId="kpi-occupation" onClick={() => ouvrir("occupation_atelier")} sub={<TendanceSub kpi={k.occupation_atelier} periode={d.periode} />} />}
      </div>
      <DetailModal detail={detail} onClose={() => setDetail(null)} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {w.objectifs_mois && (
          <Widget titre="Objectifs du mois" icon={Target} testId="widget-objectifs-mois">
            <JaugeObjectif label="Chiffre d'affaires" realise={fmtEUR(w.objectifs_mois.ca_mois ?? 0)} objectif={fmtEUR(w.objectifs_mois.ca_objectif ?? 0)}
              pct={w.objectifs_mois.atteinte_ca_pct} prorata={w.objectifs_mois.prorata_pct} testId="jauge-obj-ca" />
            <JaugeObjectif label="Livraisons" realise={`${w.objectifs_mois.livraisons_mois ?? 0}`} objectif={`${w.objectifs_mois.livraisons_objectif ?? 0}`}
              pct={w.objectifs_mois.atteinte_livraisons_pct} prorata={w.objectifs_mois.prorata_pct} testId="jauge-obj-livraisons" />
            <p className="text-[11px] text-[--idc-muted] mt-1.5">
              Le repère vertical marque le prorata du temps écoulé ({w.objectifs_mois.prorata_pct} % du mois). Objectifs toujours mensuels, quel que soit le filtre de période.
            </p>
          </Widget>
        )}
        {w.ca_activites && (
          <Widget titre="CA par activité" icon={PieChart} testId="widget-ca-activites">
            <div className="grid grid-cols-2 gap-2">
              {ACTIVITES.map(([cle, label, coul]) => (
                <div key={cle} className="rounded-lg p-3" style={{ background: `${coul}10` }} data-testid={`ca-${cle}`}>
                  <div className="text-[11px] font-bold" style={{ color: coul }}>{label}</div>
                  <div className="text-[16px] font-extrabold">{fmtEUR(w.ca_activites[cle] || 0)}</div>
                </div>
              ))}
            </div>
          </Widget>
        )}
        {w.pipeline_synthese && (
          <Widget titre="Pipeline — synthèse" icon={KanbanSquare} testId="widget-pipeline-synthese">
            {Object.entries(w.pipeline_synthese).map(([e, v]) => (
              <Ligne key={e} onClick={() => navigate("/pipeline")} testId={`pipe-${e}`}>
                <span className="text-[13px] font-medium w-24">{ETAPES[e] || e}</span>
                <span className="pill" style={{ background: "#EAF1FE", color: "#2F6FED" }}>{v.nb}</span>
                <span className="text-[12px] text-[--idc-muted] ml-auto">{fmtEUR(v.montant)}</span>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.atelier_synthese && (
          <Widget titre="Activité atelier" icon={Wrench} testId="widget-atelier-synthese">
            <div className="grid grid-cols-3 gap-2">
              {[["ouverts", "Ouverts", "#2F6FED"], ["en_cours", "En cours", "#E8853D"], ["termines", "Terminés", "#16A34A"]].map(([cle, label, coul]) => (
                <div key={cle} className="rounded-lg p-3 text-center" style={{ background: `${coul}10` }}>
                  <div className="text-[20px] font-extrabold" style={{ color: coul }}>{w.atelier_synthese[cle]}</div>
                  <div className="text-[11px] text-[--idc-muted]">{label}</div>
                </div>
              ))}
            </div>
          </Widget>
        )}
        {w.encours_administratif && (
          <Widget titre="Encours administratif" icon={FolderKanban} testId="widget-encours-administratif">
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="rounded-lg p-3" style={{ background: "#FED7AA30" }} data-testid="encours-adm-livres-nf">
                <div className="text-[11px] font-bold text-[#9A3412]">Livrés non facturés</div>
                <div className="text-[16px] font-extrabold">{w.encours_administratif.livres_non_factures.nb} · {fmtEUR(w.encours_administratif.livres_non_factures.montant)}</div>
              </div>
              <div className="rounded-lg p-3" style={{ background: "#DBEAFE50" }} data-testid="encours-adm-delai">
                <div className="text-[11px] font-bold text-[#1E3A8A]">Délai signature → facturation</div>
                <div className="text-[16px] font-extrabold">{w.encours_administratif.delai_moyen_signature_facturation_j != null ? `${w.encours_administratif.delai_moyen_signature_facturation_j} j` : "—"}</div>
              </div>
            </div>
            {Object.entries(w.encours_administratif.par_statut || {}).map(([s, n]) => (
              <div key={s} className="flex items-center justify-between py-1 text-[12.5px]" data-testid={`encours-adm-${s}`}>
                <span>{STATUTS_ADM[s] || s}</span>
                <span className="font-bold">{n}</span>
              </div>
            ))}
          </Widget>
        )}
        {w.impayes && (
          <Widget titre="Impayés" icon={AlertCircle} testId="widget-impayes" vide={w.impayes.length === 0 && "Aucun impayé. 👍"}>
            {w.impayes.map((f) => (
              <Ligne key={f.id} onClick={() => navigate(`/facturation/${f.id}`)} testId={`impaye-${f.numero}`}>
                <span className="font-mono text-[12px] shrink-0">{f.numero}</span>
                <span className="text-[13px] truncate">{f.client}</span>
                <span className="pill shrink-0" style={{ background: "#FEE2E2", color: "#B91C1C" }}>J+{f.jours}</span>
                <span className="text-[12px] font-semibold ml-auto shrink-0">{fmtEUR(f.montant)}</span>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.stock_tranches && (
          <Widget titre="Stock véhicules par tranche d'âge" icon={CarFront} testId="widget-stock-tranches">
            {Object.entries(w.stock_tranches).map(([t, n]) => (
              <div key={t} className="flex items-center gap-3 py-1.5">
                <span className="text-[12.5px] w-16">{t}</span>
                <div className="flex-1 h-2.5 bg-[#EEF1F6] rounded-full">
                  <div className="h-2.5 rounded-full" style={{ width: `${Math.min(n * 12, 100)}%`, background: t === "> 90 j" ? "#DC2626" : t === "61-90 j" ? "#E8853D" : "#2F6FED" }} />
                </div>
                <span className="text-[13px] font-bold w-6 text-right">{n}</span>
              </div>
            ))}
          </Widget>
        )}
      </div>
    </div>
  );
};
